
import { motion } from "framer-motion";
import { ArrowRight, Heart, Scissors, ChevronDown } from "lucide-react";
import { Link } from "react-router";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";
import ThreadDecoration from "@/components/ui/ThreadDecoration";

export default function HeroSection() {
  const theme = usePalette();
  return (
    <section
      className="relative min-h-dvh flex items-center overflow-hidden pt-20"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-96 h-96 top-10 -left-20" color={theme.blob1} />
      <Blob className="w-80 h-80 bottom-10 right-0" color={theme.blob2} />
      <Blob className="w-64 h-64 top-1/2 left-1/3" color={theme.blob3} />
      <StitchDots />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text side */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center lg:text-left"
          >
            <motion.h1
              variants={fadeUp}
              custom={0}
              className="text-5xl sm:text-6xl lg:text-7xl leading-[1.08] mb-6"
              style={{
                fontFamily: fonts.logo,
                fontWeight: 400,
                color: colors.navy,
              }}
            >
              Loose threads,{" "}
              <span
                className="relative inline-block"
                style={{ color: theme.accent }}
              >
                big ideas
                <motion.svg
                  className="absolute -bottom-1 left-0 w-full"
                  viewBox="0 0 200 12"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                >
                  <motion.path
                    d="M2 8 Q50 2, 100 7 T198 5"
                    stroke={theme.warm}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="6 4"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                  />
                </motion.svg>
              </span>
              <span style={{ color: theme.warm }}>!</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={1}
              className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: colors.navy,
                opacity: 0.75,
                letterSpacing: "0.03em",
              }}
            >
              Step away from the noise and into a weekend of sewing, creativity,
              and connection at my next retreat!
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={2}
              className="flex items-center gap-4 justify-center lg:justify-start"
            >
              <Link
                to="/retreats"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white text-sm tracking-wide cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
                style={{
                  fontFamily: fonts.button,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase" as const,
                  fontSize: "0.75rem",
                  backgroundColor: theme.accent,
                }}
              >
                Explore Retreats
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>

          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{
              duration: 0.7,
              delay: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="relative flex justify-center lg:justify-end"
          >
            <div
              className="absolute -inset-4 rounded-[2.5rem] -rotate-3"
              style={{
                background: `linear-gradient(135deg, ${theme.blob1}40, ${theme.blob2}30, ${theme.blob3}40)`,
              }}
            />
            <div className="relative">
              <img
                src="/chelsea-and-cosmo.jpg"
                alt="Chelsea and Cosmo — sewist, creator, and community builder"
                width={448}
                height={597}
                className="relative z-10 w-full max-w-md rounded-[2rem] shadow-2xl object-cover aspect-[3/4]"
                style={{
                  boxShadow: `0 25px 50px -12px ${theme.blob1}60`,
                }}
              />
              <motion.div
                className="absolute -top-4 -right-4 z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: colors.cream }}
                animate={{ y: [0, -6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut",
                }}
              >
                <Heart
                  size={18}
                  style={{ color: colors.berryPink }}
                  fill={colors.berryPink}
                />
                <span
                  className="text-sm"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                  }}
                >
                  200K Fam
                </span>
              </motion.div>
              <motion.div
                className="absolute -bottom-3 -left-4 z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: colors.cream }}
                animate={{ y: [0, 6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3.5,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              >
                <Scissors size={18} style={{ color: theme.accent }} />
                <span
                  className="text-sm"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                  }}
                >
                  Creating Daily
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-16 left-0 right-0 z-10 px-12">
        <ThreadDecoration className="w-full opacity-40" />
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
      >
        <ChevronDown size={24} style={{ color: `${colors.navy}40` }} />
      </motion.div>
    </section>
  );
}
